import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import React, { memo, PropsWithChildren } from 'react';
import { Info } from 'react-feather';
import Select from 'src/components/Inputs/Select';
import TextField from 'src/components/Inputs/TextField';
import Utility from 'src/components/Inputs/Utility';
import Text from 'src/components/Text/Text';
import Env from 'src/environmentVariables/EnvironmentVariables';
import TradeFormInputLabel from './TradeFormInputLabel';
import Tooltip from '../Tooltip/Tooltip';
import { UIOrderType } from '@drift/common';
import { twMerge } from 'tailwind-merge';

export const postOnlyTooltip =
	"Post Only: Enforces that order is a maker order that can provide liquidity to the pool for a rebate. Without this flag, orders that don't execute immediate are not post only.";
export const reduceOnlyTooltip =
	'Reduce Only: Enforces that order will never increase or flip the current position.';
const _iocTooltip =
	'Immediate or cancel (IOC): An order is placed and potentially partially filled. The remainder that is not filled immediately is then cancelled.';
export const postOnlyAndReduceOnlyNotAllowedTooltip = `You can't use Reduce Only and Post Only on the same order.`;

export const FieldSplitter = (props: PropsWithChildren<any>) => {
	return (
		<div className="flex items-end justify-between space-x-2">
			{props.children}
		</div>
	);
};

export const LabelAndField = (props: PropsWithChildren<any>) => {
	return (
		<div
			className={twMerge('flex flex-col', props.customWidthClass ?? 'w-1/2')}
		>
			{props.children}
		</div>
	);
};

export interface OrderTypeSelectorProps {
	orderType: string;
	priceBoxHeading: string;
	priceBoxEnabled: boolean;
	priceBoxStringValue: string;
	minPriceInc: number;
	orderTypeOptions: any;
	onOrderTypeChange: (newValue: any) => void;
	onPriceBoxChange: (newValue: any) => void;
	onPriceBoxSave?: (newValue: any) => void;
}

export const OrderTypeSelector = memo(function OrderTypeSelectorMemo({
	orderType,
	priceBoxHeading,
	priceBoxStringValue,
	priceBoxEnabled,
	minPriceInc,
	orderTypeOptions,
	onPriceBoxChange,
	onOrderTypeChange,
	onPriceBoxSave,
}: OrderTypeSelectorProps) {
	return (
		<FieldSplitter>
			<LabelAndField>
				<TradeFormInputLabel className="mt-0">Order Type</TradeFormInputLabel>
				<Select.Form
					id="marketSelection"
					selection={orderType}
					options={orderTypeOptions}
					onChange={onOrderTypeChange}
					useFullWidth
				/>
			</LabelAndField>
			<LabelAndField>
				<TradeFormInputLabel className="mt-0">
					{' '}
					{priceBoxHeading}
				</TradeFormInputLabel>

				{priceBoxEnabled ? (
					<TextField.Default
						value={priceBoxStringValue}
						disabled={!priceBoxEnabled}
						type="number"
						stepAmount={minPriceInc}
						onChange={onPriceBoxChange}
						suffix={'USD'}
						onBlur={() => onPriceBoxSave(priceBoxStringValue)}
					/>
				) : (
					<div className="pointer-events-none bg-input-bg rounded-sm w-full px-2 flex items-center text-text-default h-[32px] select-none">
						Market Price
					</div>
				)}
			</LabelAndField>
		</FieldSplitter>
	);
});

interface SecondaryPriceBoxProps {
	secondaryPriceBoxStringValue: string;
	minPriceInc: number;
	onSecondaryPriceBoxChange: (value: string) => void;
}

export const SecondaryPriceBox = memo(function SecondaryPriceBoxMemo({
	secondaryPriceBoxStringValue,
	minPriceInc,
	onSecondaryPriceBoxChange,
}: SecondaryPriceBoxProps) {
	return (
		<>
			<div className="flex justify-between space-x-2">
				<div className="flex flex-col" style={{ flexGrow: 2 }}>
					<TradeFormInputLabel>Limit Price</TradeFormInputLabel>
					<TextField.Default
						value={secondaryPriceBoxStringValue}
						disabled={false}
						type="number"
						stepAmount={minPriceInc}
						onChange={onSecondaryPriceBoxChange}
						suffix={'USD'}
					/>
				</div>
			</div>
			<Utility.VERTSPACERL />
		</>
	);
});

export interface SizeSelectorProps {
	reduceOnly: boolean;
	baseSizeStringValue: string;
	formattedMarketSymbol: string;
	quoteSizeStringValue: string;
	maxQuoteAvailable: number;
	maxBaseAvailableString: string;
	currentPositionBazeSizeString: string;
	onBaseSizeChange: (value: string) => void;
	handleQuoteSizeChange: (value: string) => void;
	showMaxButton: boolean;
	stepSize: number;
	orderType: UIOrderType;
	userHasSelfImposedMax?: boolean;
}

export const SizeSelector = memo(function SizeSelectorMemo({
	reduceOnly,
	baseSizeStringValue,
	formattedMarketSymbol,
	quoteSizeStringValue,
	maxQuoteAvailable,
	maxBaseAvailableString,
	currentPositionBazeSizeString,
	onBaseSizeChange,
	handleQuoteSizeChange,
	showMaxButton,
	stepSize,
	orderType,
	userHasSelfImposedMax,
}: SizeSelectorProps) {
	const isOracleLimitOrder = orderType === 'oracleLimit';

	return (
		<>
			<FieldSplitter>
				<LabelAndField
					customWidthClass={isOracleLimitOrder ? 'w-full' : undefined}
				>
					<Text.BODY3>
						<div className={'flex justify-between text-text-label'}>
							<div className="flex jusitfy-start pb-0.5">Size</div>
							{isOracleLimitOrder && (
								<div className="flex items-center justify-end">
									<div
										className="flex items-end space-x-2 hover:cursor-pointer text-text-emphasis"
										onClick={() => {
											onBaseSizeChange(
												reduceOnly
													? currentPositionBazeSizeString
													: maxBaseAvailableString
											);
										}}
									>
										<div
											className={`flex items-center font-numeral bg-button-secondary-bg hover:bg-button-secondary-bg-hover hover:cursor-pointer px-1 pt-0.5 pb-0 mb-1 rounded-sm select-none text-text-label tracking-normal`}
											style={{ fontSize: '10px' }}
										>
											<div className="mr-2">
												{reduceOnly ? 'Position:' : 'Max:'}
											</div>
											<span className="mr-1">
												{reduceOnly
													? currentPositionBazeSizeString
													: maxBaseAvailableString}
											</span>
											<span style={{ fontSize: '10px' }}>
												{formattedMarketSymbol}
											</span>
										</div>
									</div>
									{userHasSelfImposedMax && (
										<Tooltip
											content={`Your max amount shown here is lower than the protocol allows due to self imposed max account leverage. You can adjust this in settings.`}
										>
											<Info className="mb-1.5 ml-0.5" size={12} />
										</Tooltip>
									)}
								</div>
							)}
						</div>
					</Text.BODY3>

					<TextField.Default
						type="number"
						onChange={onBaseSizeChange}
						value={baseSizeStringValue}
						showIconForMarketSymbol={formattedMarketSymbol}
						disabled={maxQuoteAvailable <= 0}
						stepSize={stepSize}
						depsForOnChange={[stepSize]}
					/>
				</LabelAndField>

				{!isOracleLimitOrder && (
					<LabelAndField>
						{showMaxButton && (
							<TradeFormInputLabel allowPointer className={'flex items-end'}>
								<div
									className="flex items-end space-x-2 hover:cursor-pointer text-text-emphasis"
									onClick={() => {
										reduceOnly
											? onBaseSizeChange(currentPositionBazeSizeString)
											: handleQuoteSizeChange(maxQuoteAvailable.toFixed(2));
									}}
								>
									<div
										className={`flex items-center font-numeral bg-button-secondary-bg hover:bg-button-secondary-bg-hover hover:cursor-pointer px-1 pt-0.5 pb-0 mb-1 rounded-sm select-none text-text-label tracking-normal`}
										style={{ fontSize: '10px' }}
									>
										<div className="mr-2">
											{reduceOnly ? 'Position:' : 'Max:'}
										</div>
										<span className="mr-1">
											{reduceOnly
												? currentPositionBazeSizeString
												: Math.max(maxQuoteAvailable, 0).toLocaleString(
														Env.locale,
														{
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														}
												  )}
										</span>
										<span style={{ fontSize: '10px' }}>
											{reduceOnly ? `${formattedMarketSymbol}` : `USD`}
										</span>
									</div>
								</div>
								{userHasSelfImposedMax && (
									<Tooltip
										content={`Your max amount shown here is lower than the protocol allows due to self imposed max account leverage. You can adjust this in settings.`}
									>
										<Info className="mb-1.5 ml-0.5" size={12} />
									</Tooltip>
								)}
							</TradeFormInputLabel>
						)}

						<TextField.Default
							type="number"
							onChange={handleQuoteSizeChange}
							onBlur={() =>
								handleQuoteSizeChange(
									BigNum.fromPrint(
										quoteSizeStringValue,
										QUOTE_PRECISION_EXP
									).toFixed(2)
								)
							}
							value={isOracleLimitOrder ? '' : quoteSizeStringValue}
							showIconForMarketSymbol={'USDC'}
							disabled={isOracleLimitOrder || maxQuoteAvailable <= 0}
						/>
					</LabelAndField>
				)}
			</FieldSplitter>
		</>
	);
});
